import { useAssetDetailsContext } from "@/pages/asset-details/context/assetDetailsContext";
import {
	addLinkAssetLOTO,
	removeLOTOLink,
} from "@/pages/asset-details/repositories/AssetDetailsRepositorie";
import type { GridApi } from "ag-grid-community";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const useAssetLOTOsGridActionsAndButtons = ({
	gridRef,
	searchStatistics,
	searchAssetLOTO,
	setIsLinkLOTOLoading,
	setLinkAssetLOTOError,
	setIsRemoveLOTOLoading,
	setRemoveAssetLOTOError,
	includeDescendants,
}: {
	gridRef: GridApi | null;
	searchStatistics: Function;
	searchAssetLOTO: Function;
	setIsLinkLOTOLoading: (isLoading: boolean) => void;
	setLinkAssetLOTOError: (err: string) => void;
	setIsRemoveLOTOLoading: (isLoading: boolean) => void;
	setRemoveAssetLOTOError: (err: string) => void;
	includeDescendants: boolean;
}) => {
	const {
		state: {
			assetSearchId,
			assetSearchName,
			LOTONumberToLink,
			LOTONameToLink,
			listAssetLOTO,
			assetLOTOComments,
			rowSelected,
		},
		actions: {
			setFirstTime,
			setLOTONumberToLink,
			setLOTONameToLink,
			setListAssetLOTO,
		},
	} = useAssetDetailsContext();

	const [refreshTrigger, setRefreshTrigger] = useState(1);

	// BUTTONS STATES
	const [addButton, setAddButton] = useState<boolean>(true);
	const [deleteButton, setDeleteButton] = useState<boolean>(false);
	const [saveButton, setSaveButton] = useState<boolean>(false);
	const [discardButton, setDiscardButton] = useState<boolean>(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const isMountRef = useRef(true);
	const [searchParams] = useSearchParams();

	const refresh = useCallback(() => {
		if (!isMountRef.current) {
			setFirstTime(true);
			setSaveButton(false);
			setDiscardButton(false);
			setAddButton(true);
			setDeleteButton(false);
			setDeleteOpen(false);
			setLOTONumberToLink("");
			setLOTONameToLink("");
			searchAssetLOTO(includeDescendants);
			searchStatistics();
		}
	}, [assetSearchId, searchParams, includeDescendants]);

	const saveRow = useCallback(async () => {
		if (LOTONumberToLink === "" || LOTONameToLink === "") {
			setLinkAssetLOTOError("LOTO number and LOTO name are required");
		} else {
			setIsLinkLOTOLoading(true);
			const addLOTOLink = await addLinkAssetLOTO(
				assetSearchId as string,
				LOTONumberToLink,
				LOTONameToLink,
				assetLOTOComments,
			);
			if (addLOTOLink.ok) {
				setIsLinkLOTOLoading(false);
				setListAssetLOTO([]);
			} else {
				setIsLinkLOTOLoading(false);
				setLinkAssetLOTOError(addLOTOLink.message);
			}
			setRefreshTrigger((prev) => prev + 1);
		}
	}, [
		refresh,
		assetSearchId,
		assetSearchName,
		LOTONumberToLink,
		LOTONameToLink,
		assetLOTOComments,
	]);

	const addRow = useCallback(() => {
		setAddButton(false);
		setSaveButton(true);
		setDiscardButton(true);
		setDeleteButton(false);
		const newData = { id: -1, AssetId: assetSearchId };
		const currentData = listAssetLOTO ? listAssetLOTO : [];
		const data = [newData, ...currentData];
		setListAssetLOTO(data);
	}, [listAssetLOTO, assetSearchId]);

	const removeSelected = useCallback(async () => {
		if (listAssetLOTO) {
			setIsRemoveLOTOLoading(true);
			const selectedRowNodes = gridRef?.getSelectedNodes();
			if (selectedRowNodes?.length) {
				selectedRowNodes.forEach(async (LOTOLink) => {
					const removeLOTOFromAsset = await removeLOTOLink(
						assetSearchId,
						LOTOLink.data.LOTONumber,
					);
					if (!removeLOTOFromAsset.ok) {
						setRemoveAssetLOTOError(removeLOTOFromAsset.message);
					}
				});
			} else if (rowSelected) {
				const removeDrawing = await removeLOTOLink(
					assetSearchId as any,
					rowSelected.LOTONumber,
				);
				if (!removeDrawing.ok) {
					setRemoveAssetLOTOError(removeDrawing.message);
				}
			}
			setIsRemoveLOTOLoading(false);
			setRefreshTrigger((prev) => prev + 1);
		}
	}, [listAssetLOTO, assetSearchId, refresh, gridRef, rowSelected]);

	useEffect(() => {
		refresh();
	}, [refreshTrigger]);

	useEffect(() => {
		isMountRef.current = false;
	}, []);

	return {
		refresh,
		saveRow,
		addRow,
		removeSelected,
		addButton,
		deleteButton,
		saveButton,
		discardButton,
		deleteOpen,
		setSaveButton,
		setDiscardButton,
		setAddButton,
		setDeleteButton,
		setDeleteOpen,
	};
};
